import { z } from 'zod'
import { timestampToDate, timestampToDateNullable } from '@/core/firebase/converters'

/**
 * One row per automatic or manual mockup deletion. Top-level collection
 * `mockup_cleanup_log/{id}`. Written through the admin SDK (which bypasses
 * Firestore rules); admin-only read for sales attribution.
 *
 * The natural key is `clubId` — by the time anyone queries this row, the
 * `clubs/{clubId}` doc no longer exists. `clubName` + `sentToEmails` are
 * denormalised so a digest reader can identify the prospect without
 * cross-collection joins.
 */
export const MockupCleanupLogEntrySchema = z.object({
  id: z.string(),
  clubId: z.string(),
  clubName: z.string().default(''),
  deletedAt: timestampToDate,
  /** Automatic policy verdict or a verified manual no-activity cleanup. */
  reason: z.enum(['expired', 'orphan', 'invite_ignored', 'manual_no_activity']),
  /** Latest non-revoked token expiry at deletion time. Null when the
   * club had no tokens (orphan). */
  lastTokenExpiredAt: timestampToDateNullable.nullable().optional(),
  /** Deduped recipient emails across all the tokens — sales attribution. */
  sentToEmails: z.array(z.string()).default([]),
})

export type MockupCleanupLogEntry = z.infer<typeof MockupCleanupLogEntrySchema>

export const MOCKUP_CLEANUP_REASON_LABELS: Record<MockupCleanupLogEntry['reason'], string> = {
  expired: 'Tokens caducados',
  orphan: 'Nunca distribuida',
  invite_ignored: 'Invitación ignorada',
  manual_no_activity: 'Sin actividad',
}
